import { useLocation } from "react-router-dom";

export const useSafeLocation = () => {
  try {
    return useLocation();
  } catch (error) {
    return null;
  }
};

/**
 * Determine the base path from a URL pathname (recruiter, retention, or archived).
 * @param {string} pathname - The URL pathname
 * @returns {string} The base path ('/recruiter', '/retention', or '/archived')
 */
export const getBasePath = (pathname) => {
  if (pathname.startsWith("/retention")) {
    return "/retention";
  }
  if (pathname.startsWith("/archived")) {
    return "/archived";
  }
  return "/recruiter";
};

// BK-2907: Pipeline tab → target route map for the contacts (leads) family.
//
// Clicking a pipeline tab on the Advanced Filter Results view navigates the
// whole app to the matching module route so URL / theme / sidebar / module
// stay in sync with the active pipeline — matching the legacy data-table
// navigation behavior. Scope is preserved:
//   - `today` scope: /recruiter/today ↔ /retention/today (no /archived/today)
//   - `leads` scope: /recruiter/leads ↔ /retention/team ↔ /archived
//
// Unknown pageTypes fall back to the leads scope.
const LEAD_FAMILY_ROUTES = {
  today: {
    recruiting: "/recruiter/today",
    retention: "/retention/today",
    archived: "/archived",
  },
  leads: {
    recruiting: "/recruiter/leads",
    retention: "/retention/team",
    archived: "/archived",
  },
};

/**
 * Compute the target pathname for a pipeline tab click on a contacts-family
 * page. The destination route depends on TWO things: which product the user
 * clicked (recruiting/retention/archived) and which "scope" the user is
 * currently in (the today widget landing vs the full leads table). Scope is
 * derived from the current pathname at click time — it is intentionally NOT a
 * pageType, because every contacts route in BrokerKit (today, leads, team,
 * archived, search, backlog, saved_searches) is the same `contacts` entity.
 *
 * Scope preservation matches the user's mental model: a Today user clicking
 * Retention should land on Retention's Today, not be dumped on the team table.
 *
 * @param {string} currentPathname - history.location.pathname
 * @param {string} targetProduct - clicked tab key (`recruiting`, `retention`,
 *   `archived`)
 * @returns {string|null} target pathname
 */
export const routeForLeadFamilyTab = (currentPathname, targetProduct) => {
  const scope = (currentPathname || "").includes("/today") ? "today" : "leads";
  return LEAD_FAMILY_ROUTES[scope][targetProduct] || null;
};

/**
 * True when the pageType belongs to the contacts (lead) entity. Only one
 * pageType qualifies — `contacts`. The contacts entity surfaces in 13 routes
 * across three products and several scopes (today/leads/search/backlog/
 * saved_searches), but they all share the same pageType, the same filter set,
 * and the same localStorage bucket.
 *
 * `today`, `retention`, `archive` are NOT pageTypes — `today` is a route scope
 * (widget-first landing view) and `retention` / `archive` are products (URL
 * theme).
 *
 * @param {string} pageType
 * @returns {boolean}
 */
export const isLeadFamilyPage = (pageType) => pageType === "contacts";

// BK-2907: URL query param that marks the Advanced Filter Results view as the
// active sub-view for the contacts family. The URL — not component state — is
// the source of truth for "am I in the results view" ONLY on lead-family
// routes, so that clicking a pipeline tab can change the pathname while
// keeping the view flag carried over on the new route. All other entity
// families (tasks/docs/videos/jobs/candidates) stay state-flag driven and
// never emit this param.
const ADVANCED_FILTER_VIEW_PARAM = "view";
const ADVANCED_FILTER_VIEW_VALUE = "advanced_filter";

/**
 * True when the current URL search string indicates the Advanced Filter
 * Results view should stay active (carried across pipeline tab navigation).
 * @param {string} search - location.search (including leading '?', or empty)
 * @returns {boolean}
 */
export const isAdvancedFilterResultsRoute = (search) => {
  try {
    return (
      new URLSearchParams(search || "").get(ADVANCED_FILTER_VIEW_PARAM) ===
      ADVANCED_FILTER_VIEW_VALUE
    );
  } catch {
    return false;
  }
};

/**
 * Returns a new search string with the advanced-filter view param set,
 * preserving any other existing query parameters.
 * @param {string} search - location.search (including leading '?', or empty)
 * @returns {string} new search string including the leading '?', or empty
 */
export const withAdvancedFilterView = (search = "") => {
  const params = new URLSearchParams(search || "");
  params.set(ADVANCED_FILTER_VIEW_PARAM, ADVANCED_FILTER_VIEW_VALUE);
  const str = params.toString();
  return str ? `?${str}` : "";
};

/**
 * Returns a new search string with the advanced-filter view param removed,
 * preserving any other existing query parameters.
 * @param {string} search - location.search (including leading '?', or empty)
 * @returns {string} new search string including the leading '?', or empty
 */
export const withoutAdvancedFilterView = (search = "") => {
  const params = new URLSearchParams(search || "");
  params.delete(ADVANCED_FILTER_VIEW_PARAM);
  const str = params.toString();
  return str ? `?${str}` : "";
};

// BK-2907: URL query param that identifies which saved search is currently
// open in the SavedSearchesCard results view. Saved searches are
// product-agnostic data — the same saved search exists across recruiter,
// retention, and archived modules — so the saved-search ID lives in a URL
// param that survives cross-product navigation. The destination route
// (`/{product}/saved_searches`) reads this param on mount, fetches the
// saved search by id, and re-opens the results view automatically.
const SAVED_SEARCH_PARAM = "saved_search";
const TEAM_CONTEXT_PARAM = "_t";

/**
 * Read the saved-search ID from a URL search string. Returns null if absent
 * or malformed.
 * @param {string} search - location.search (including leading '?', or empty)
 * @returns {string|null}
 */
export const getSavedSearchIdFromSearch = (search = "") => {
  try {
    return new URLSearchParams(search || "").get(SAVED_SEARCH_PARAM);
  } catch {
    return null;
  }
};

/**
 * Returns a new search string with the saved-search id param set,
 * preserving any other existing query parameters.
 */
export const withSavedSearchId = (search = "", id) => {
  const params = new URLSearchParams(search || "");
  params.set(SAVED_SEARCH_PARAM, String(id));
  const str = params.toString();
  return str ? `?${str}` : "";
};

export const withSavedSearchTeamContext = (search = "", teamId) => {
  const params = new URLSearchParams(search || "");
  if (teamId === null || teamId === undefined || teamId === "") {
    params.delete(TEAM_CONTEXT_PARAM);
  } else {
    params.set(TEAM_CONTEXT_PARAM, String(teamId));
  }
  const str = params.toString();
  return str ? `?${str}` : "";
};

/**
 * Returns a new search string with the saved-search id param removed.
 */
export const withoutSavedSearchId = (search = "") => {
  const params = new URLSearchParams(search || "");
  params.delete(SAVED_SEARCH_PARAM);
  params.delete(TEAM_CONTEXT_PARAM);
  const str = params.toString();
  return str ? `?${str}` : "";
};

/**
 * Pipeline tab → target saved-searches route map.
 *
 * Saved searches are product-agnostic — the same data is shown under each
 * module's theme. Clicking a pipeline tab on a saved-search results view
 * navigates to the matching product's `/saved_searches` route while carrying
 * the `?saved_search=<id>` param, so the destination's SavedSearchesCard
 * re-opens the same saved search under the new theme.
 *
 * Unlike `routeForLeadFamilyTab`, there is no today/leads scope here —
 * saved searches don't have a today equivalent.
 *
 * @param {string} targetProduct - clicked tab key (`recruiting`, `retention`,
 *   `archived`)
 * @returns {string|null} target pathname for the destination saved-searches
 *   page, or null if the product is unknown.
 */
const SAVED_SEARCH_ROUTES = {
  recruiting: "/recruiter/saved_searches",
  retention: "/retention/saved_searches",
  archived: "/archived/saved_searches",
};

export const routeForSavedSearchTab = (targetProduct) =>
  SAVED_SEARCH_ROUTES[targetProduct] || null;

/**
 * Remove specified URL parameters from the current URL without page reload
 * @param {string[]} params - Array of parameter names to remove
 */
export const clearUrlParams = (params) => {
  const searchParams = new URLSearchParams(window.location.search);
  let hasChanges = false;

  params.forEach((param) => {
    if (searchParams.has(param)) {
      searchParams.delete(param);
      hasChanges = true;
    }
  });

  if (hasChanges) {
    const newPath = `${window.location.pathname}${searchParams.toString() ? `?${searchParams}` : ""}`;
    window.history.replaceState({}, "", newPath);
  }
};
